<template>

  <div>

    <permission-list-add-new
      :is-add-new-permission-sidebar-active.sync="isAddNewPermissionSidebarActive"
      @refetch-data="refetchData"
    />

    <!-- Filters -->
    <permissions-list-filters
      :status-filter.sync="statusFilter"
      :status-options="statusOptions"
      class="d-none"
    />

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >

            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
            <b-button
              class="ml-2"
              variant="primary"
              @click="isAddNewPermissionSidebarActive = true"
            >
              <span class="text-nowrap">Add Permission</span>
            </b-button>
          </b-col>
        </b-row>

      </div>

      <b-table
        ref="refPermissionListTable"
        class="position-relative"
        :items="fetchPermissions"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
      >

        <!-- Column: Permission -->
        <template #cell(permission)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.logo_url"
                :text="avatarText(data.item.name)"
                :variant="`light-${resolvePermissionRoleVariant(data.item.role)}`"
                :to="{ name: 'permissions-view', params: { id: data.item.id } }"
              />
            </template>
            <b-link
              :to="{ name: 'permissions-view', params: { id: data.item.id } }"
              class="font-weight-bold d-block text-nowrap"
            >
              {{ data.item.name }}
            </b-link>
            <b-badge
              v-if="data.item.enabled"
              pill
              :variant="`light-${resolvePermissionStatusVariant(data.item.enabled)}`"
              class="text-capitalize"
            >
              Active
            </b-badge>
            <b-badge
              v-else
              pill
              :variant="`light-${resolvePermissionStatusVariant(data.item.enabled)}`"
              class="text-capitalize"
            >
              Inactive
            </b-badge>
          </b-media>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item :to="{ name: 'permissions-view', params: { id: data.item.id } }">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Details</span>
            </b-dropdown-item>

            <b-dropdown-item :to="{ name: 'permissions-edit', params: { id: data.item.id } }">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item-button @click="openDeleteModal(data.item, deleteTargetItem)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item-button>
          </b-dropdown>
        </template>

      </b-table>
      <div class="mx-2 mb-2">
        <b-row>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >

            <b-pagination
              v-model="currentPage"
              :total-rows="totalPermissions"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>

          </b-col>

        </b-row>
      </div>
    </b-card>

    <b-modal id="modalDelete">
      <template #modal-header="{}">
        <h5>Confirm deletion</h5>
      </template>


      Are you sure you want to delete the permission {{ deleteTargetItem.name }}?

      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="primary" @click="cancel()">
          Cancel
        </b-button>

        <b-button size="sm" variant="danger" @click="deleteTarget(deleteTargetItem.id); cancel();">
          Delete
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BModal, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BDropdownItemButton, BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { ref } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import PermissionsListFilters from './PermissionsListFilters.vue'
import usePermissionsList from './usePermissionsList'
import PermissionListAddNew from './PermissionListAddNew.vue'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    PermissionsListFilters,
    PermissionListAddNew,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownItemButton,
    BModal,
    BPagination,

    vSelect,
  },
  methods: {
    openDeleteModal(item, targetRef) {
      // Update modal data then open it
      targetRef.id = item.id
      targetRef.name = item.name
      this.$bvModal.show('modalDelete')
    },
  },
  setup() {
    const {
      fetchPermissions,
      tableColumns,
      perPage,
      currentPage,
      totalPermissions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPermissionListTable,
      refetchData,

      // UI
      resolvePermissionRoleVariant,
      resolvePermissionRoleIcon,
      resolvePermissionStatusVariant,

      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,
    } = usePermissionsList()

    // Use toast
    const toast = useToast()

    const isAddNewPermissionSidebarActive = ref(false)

    const statusOptions = [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const deleteTargetItem = ref({ id : null, name : null })

    const deleteTarget = (targetId) => {
      store.dispatch('app/deletePermission', { id: targetId })
        .then(() => {
          refetchData()
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error deleting permission group.',
              text: 'ERROR: ' + error.response.data.message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }



    return {

      // Sidebar
      isAddNewPermissionSidebarActive,

      fetchPermissions,
      tableColumns,
      perPage,
      currentPage,
      totalPermissions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPermissionListTable,
      refetchData,

      deleteTargetItem,
      deleteTarget,

      // Filter
      avatarText,

      // UI
      resolvePermissionRoleVariant,
      resolvePermissionRoleIcon,
      resolvePermissionStatusVariant,

      statusOptions,

      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
